/**
 * Ce que le patron garde quand l'abonnement ne court plus : la demande de
 * reprise (bandeau et écran de suspension partagent la même lecture, pour
 * qu'une demande déjà envoyée ne soit jamais reproposée) et la récupération de
 * ses données (décision 12 du 19 octobre 2026). Quand l'archive dépasse ses
 * limites, l'API demande d'elle-même la copie à l'équipe MyEquip et l'état le
 * dit (`copyRequest`). Hors session d'assistance.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../core/api.js';
import { isCompanyOwner, useAuth } from '../../core/auth.js';

interface OpenRequest { id: string; kind: string }

/** Adresse de la récupération des données, servie avec l'écran de suspension seulement. */
export const DATA_RECOVERY_PATH = '/recuperer-mes-donnees';

const REQUESTS_KEY = ['subscription-requests'] as const;
const EXPORT_KEY = ['subscription-data-export'] as const;

/** Patron de l'entreprise, hors session d'assistance MyEquip. */
export function useIsCompanyOwner(): boolean {
  const { me } = useAuth();
  return isCompanyOwner(me) && !me?.support_session;
}

interface ResumeRequest {
  enabled: boolean;
  /** Une demande de reprise attend l'équipe MyEquip. */
  pending: boolean;
  loading: boolean;
  send: () => void;
  sending: boolean;
  error: Error | null;
}

/** Demande de reprise de l'abonnement à l'équipe MyEquip (facturation manuelle). */
export function useResumeRequest(): ResumeRequest {
  const owner = useIsCompanyOwner();
  const queryClient = useQueryClient();
  const requests = useQuery({
    queryKey: REQUESTS_KEY,
    queryFn: () => api<OpenRequest[]>('/subscription/requests'),
    enabled: owner,
  });
  const ask = useMutation({
    mutationFn: () => api<OpenRequest>('/subscription/requests', { method: 'POST', body: { kind: 'reprise' } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }),
  });
  return {
    enabled: owner,
    pending: (requests.data ?? []).some((request) => request.kind === 'reprise'),
    loading: requests.isLoading,
    send: () => ask.mutate(),
    sending: ask.isPending,
    error: ask.error as Error | null,
  };
}

export interface DataExportView {
  latest: {
    id: string;
    status: 'en_file' | 'en_cours' | 'prete' | 'echec';
    createdAt: string;
    finishedAt: string | null;
    error: string | null;
    /** `budget` : archive trop volumineuse ou trop longue, la copie est demandée à MyEquip. */
    errorKind: 'budget' | 'technique' | 'abandon' | null;
  } | null;
  /** Dernière archive hors budget : la copie demandée d'elle-même à l'équipe MyEquip. */
  copyRequest?: { status: 'ouverte' | 'traitee'; createdAt: string; processedAt: string | null } | null;
  /** Archive prête ; son lien se demande à part, identité revérifiée. */
  download: { fileName: string; sizeBytes: number; expiresAt: string } | null;
  nextAllowedAt: string | null;
  /** Abonnement terminé : dernier jour de la récupération (30 jours après la fin). */
  availableUntil?: string | null;
}

interface DownloadLink { url: string; fileName: string; sizeBytes: number; expiresAt: string }

/**
 * Le geste principal de l'état courant, un seul : patienter pendant la
 * préparation, télécharger l'archive prête, en préparer une (ou une autre
 * après un échec), ou rien quand l'archive du jour existe déjà sans être
 * téléchargeable (après un dépassement de budget, la copie est déjà demandée
 * à l'équipe MyEquip).
 */
export function recoveryStep(
  data: DataExportView | undefined,
): 'attendre' | 'telecharger' | 'preparer' | 'reessayer' | null {
  const status = data?.latest?.status;
  if (status === 'en_file' || status === 'en_cours') return 'attendre';
  if (data?.download) return 'telecharger';
  if (!data || data.nextAllowedAt) return null;
  return status === 'echec' ? 'reessayer' : 'preparer';
}

/**
 * Une archive encore valide (7 jours) n'empêche pas d'en préparer une plus
 * récente quand le serveur l'autorise (une par 24 h), notamment après un échec
 * ou pour obtenir des PDF manquants : geste secondaire, à côté de « Télécharger ».
 */
export function canPrepareAnother(data: DataExportView | undefined): boolean {
  return recoveryStep(data) === 'telecharger' && !data?.nextAllowedAt;
}

/** Archive de récupération : état, préparation (identité revérifiée par l'API) et lien. */
export function useDataExport() {
  const owner = useIsCompanyOwner();
  const queryClient = useQueryClient();
  const view = useQuery({
    queryKey: EXPORT_KEY,
    queryFn: () => api<DataExportView>('/subscription/data-export'),
    enabled: owner,
    // Pendant la préparation seulement : l'archive se prépare en quelques minutes.
    refetchInterval: (query) => {
      const status = query.state.data?.latest?.status;
      return status === 'en_file' || status === 'en_cours' ? 5_000 : false;
    },
  });
  const prepare = useMutation({
    mutationFn: () => api<DataExportView>('/subscription/data-export', { method: 'POST', body: {} }),
    onSuccess: (data) => queryClient.setQueryData(EXPORT_KEY, data),
  });
  // Le lien signé (15 minutes) se demande au moment du geste, identité
  // revérifiée par l'API (le dialogue commun s'ouvre puis rejoue la demande).
  // Archive expirée entre-temps : l'écran se met à jour avec l'état relu.
  const download = useMutation({
    mutationFn: async () => {
      const link = await api<DownloadLink>('/subscription/data-export/download', { method: 'POST', body: {} });
      window.location.assign(link.url);
    },
    onError: () => queryClient.invalidateQueries({ queryKey: EXPORT_KEY }),
  });
  return { owner, view, prepare, download };
}
